import './payment-detail.scss';

import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Badge, Button, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';

import { IRootState } from 'app/shared/reducers';
import { changePaymentStatus, getPaymentAndPatientCase, patchPayment, processPaymentOnline } from './payment.reducer';
import {
  getWatotoCareSettings as getSettings
} from 'app/modules/administration/watotocare-settings/watotocare-settings.reducer';
import { PaymentStatus } from 'app/shared/model/enumerations/payment-status.model';
import OverridePriceModal from './payment-override-price-modal';
import { fromCents, toCents } from 'app/shared/util/money-utils';
import { convertDateTimeFromServer, daysSince } from 'app/shared/util/date-utils';
import { hasAnyPermission } from 'app/shared/auth/permissions';
import { PERMISSIONS } from 'app/config/constants';
import LoadingOverlay from 'react-loading-overlay';
import { useTranslation } from 'app/shared/util/translation-utils';

export interface IPaymentDetailProps extends StateProps, DispatchProps, RouteComponentProps<{ id: string }> {
}

const PatientTypeMap = {
  'MOTHER': 'Mother',
  'BABY': 'Baby',
  'BOTH': 'Mother & Baby'
};

// eslint-disable-next-line
export const PaymentDetail = (props: IPaymentDetailProps) => {
  const { t } = useTranslation();
  // Constants
  const apiUrl = 'api/payments';

  // Props
  const { payment, watotocareSettings } = props;
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [patchingPayment, setPatchingPayment] = useState<boolean>(false);
  const journey = payment.payment.driverJourney || payment.payment.physicianJourney;
  const driverJourney = payment.payment.driverJourney;
  const physicianJourney = payment.payment.physicianJourney;
  const paymentPrice = fromCents(payment.payment.overrideAmount || payment.payment.requestedAmount);
  const paymentRequestedPrice = fromCents(payment.payment.requestedAmount || payment.payment.overrideAmount);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [driverType, setDriverType] = useState('');
  const [driverName, setDriverName] = useState('');
  const [mpesaNumber, setMpesaNumber] = useState('');

  const handleAdditionalNotes = event => {
    (
      setAdditionalNotes(event.target.value)
    );
  };

  useEffect(() => {
    props.getPaymentAndPatientCase(props.match.params.id);
    props.getSettings();
    setPatchingPayment(false);
  }, [payment.payment.status]);

  useEffect(() => {
    if (payment.updateSuccess) {
      if (payment.payment.status === 'PROCESSING_ERROR') {
        alert(t('payment.detail.processingError'));
      } else if (payment.payment.status === 'PROCESSED') {
        alert(t('payment.detail.processedSuccess'));
      }
    }
  }, [payment.payment.status, payment.updateSuccess]);

  useEffect(() => {
    if (payment.errorMessage) {
      alert(t('payment.detail.paymentFailed', { error: payment.errorMessage }));
      setPatchingPayment(false);
    }
  }, [payment.errorMessage]);

  useEffect(() => {
    if (driverJourney?.driver) {
      setDriverName(driverJourney.driver.firstName + ' ' + driverJourney.driver.lastName);
      setDriverType(t('payment.fields.driver'));
      setMpesaNumber(driverJourney.driver.mpesaNumber);
    }

    if (physicianJourney?.physician) {
      setDriverName(physicianJourney.physician.firstName + ' ' + physicianJourney.physician.lastName);
      setDriverType(t('payment.fields.physician'));
      setMpesaNumber(physicianJourney.physician.mpesaNumber);
    }
  }, [driverJourney, physicianJourney]);

  // MARK: - Event handlers
  const changeRequestStatus = (status: PaymentStatus, paymentMethod: string) => {
    setPatchingPayment(true);
    // if (paymentMethod === 'OFFLINE') {
    //   status = PaymentStatus.PROCESSED_OFFLINE;
    // }
    props.patchPayment(payment.payment.id, { status, additionalNotes, paymentMethod });
  };

  const processOnline = (status: PaymentStatus) => {
    setPatchingPayment(true);
    props.processPaymentOnline(payment.payment.id);
  };

  const overridePrice = (newPrice: number) => {
    props.patchPayment(payment.payment.id, { overrideAmount: toCents(newPrice), additionalNotes });
  };

  // Rendering
  if (payment.loading) {
    return (
      <p>{t('common.loading')}</p>
    );
  }

  let watotocareSettingsDetail = {
    paymentEditThreshold: 0,
    costKm: 0,
    flatDriverFee: 0,
    countryId: '0',
    countryName: '',
    paymentMethod: '',
    currencySymbol: ''
  };

  watotocareSettings.forEach((watotocareSetting) => {
    if (payment.payment.countryId === watotocareSetting.country.id) {
      watotocareSettingsDetail = watotocareSetting;
    }
  });
  return (
    <>
      <LoadingOverlay active={patchingPayment} spinner text={t('common.loading')}>
        <OverridePriceModal
          opened={modalOpen}
          toggle={() => {
            setModalOpen(false);
          }}
          onSave={overridePrice}
          defaultAmount={paymentPrice}
          settings={watotocareSettingsDetail}
          paymentRequestedPrice={paymentRequestedPrice}
        />
        <Row className="payment-detail-header-background p-3">
          <Col md="8">
            <Row className="justify-content-left">
              <Col md="6">
                <h4>{t('common.fields.motherDetails')}</h4>
                <div>
                  {t('common.fields.watotoCareId')}: {journey?.patientCase?.patient?.wcId}
                  <br />
                  {t('common.fields.motherName')}: {`${journey?.patientCase?.patient?.firstName} ${journey?.patientCase?.patient?.lastName}`}
                </div>
              </Col>
              <Col md="6">
                <h4>{t('common.fields.babyDetails')}</h4>
                <div>
                  {t('common.fields.dateOfBirth')}: {journey?.patientCase?.patientBaby?.birthDate} ({t('common.fields.ageInDays')}: {daysSince(journey?.patientCase?.patientBaby?.birthDate)})
                  <br />
                  {t('common.fields.babyName')}: {journey?.patientCase?.patientBaby?.firstName ? `${journey?.patientCase?.patientBaby?.firstName} ${journey?.patientCase?.patientBaby?.lastName}` : '-'}
                </div>
              </Col>
            </Row>
          </Col>
          <Col md="4">
            <Badge className="btn btn-primary float-right ml-2">{payment.payment.status}</Badge>
            <Badge
              className="btn btn-danger float-right">{convertDateTimeFromServer(payment.payment.createdDateTime)}</Badge>
          </Col>
        </Row>
        <Row className="p-3">
          <Col md="4">
            <h2 className="payment-detail-header mt-3">{t('payment.detail.journey')}</h2>
          </Col>
          <Col md="8">
            <div style={{ float: 'right' }}>
              <Badge>{t('payment.fields.route')}</Badge>{' '}
              <Badge color="primary">{journey?.totalDistance} km</Badge>{' '}
              <Badge
                color="success">{paymentPrice} {watotocareSettingsDetail.currencySymbol ? watotocareSettingsDetail.currencySymbol : 'LSL'}</Badge>
              {(payment.payment.status === PaymentStatus.REQUESTED) && (
                <Button onClick={() => {
                  setModalOpen(true);
                }} className="btn btn-primary" style={{ marginLeft: 30 }}>
                  {t('payment.detail.editPayment')}
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Row className="payment-detail-journey-cell">
          <Col>
            <h2 className="payment-detail-subtitle-header mt-2">{t('payment.detail.pickupLocation')}
              ({journey?.startHealthcareFacility ? t('common.fields.startHealthcareFacility') : journey?.startVillage ? t('common.fields.startVillage') : ''})</h2>
            <p
              className="payment-detail-subtitle">{journey?.startHealthcareFacility?.name || journey?.startVillage?.name}</p>
          </Col>
        </Row>
        <Row className="payment-detail-journey-cell">
          <Col>
            <h2 className="payment-detail-subtitle-header mt-2">{t('payment.detail.destination')}
              ({journey?.endHealthcareFacility ? t('common.fields.endHealthcareFacility') : journey?.endVillage ? t('common.fields.endVillage') : ''})</h2>
            <p
              className="payment-detail-subtitle">{journey?.endHealthcareFacility ? `${journey?.endHealthcareFacility?.name} ${journey?.endHealthcareFacility?.level}` : `${journey?.endVillage?.name}`}</p>
          </Col>
        </Row>
        {physicianJourney &&
          <>
            <Row className="payment-detail-journey-cell">
              <Col>
                <h2 className="payment-detail-subtitle-header mt-2">{t('payment.detail.physicianUnit')}</h2>
                <p className="payment-detail-subtitle">{physicianJourney.physician?.healthcareFacility?.name}</p>
              </Col>
            </Row>
          </>
        }

        {
          (journey?.incidentReportDetail?.enDescription && (
            <Row className="payment-detail-journey-cell">
              <Col>
                <h2 className="payment-detail-subtitle-header mt-2">{t('payment.detail.incidentReported')}</h2>
                <p
                  className="payment-detail-subtitle">{journey?.incidentReportDetail?.enDescription}{journey?.incidentReportDescription ? ` - ${journey?.incidentReportDescription}` : ''}</p>
              </Col>
            </Row>
          ))
        }
        <Row className="payment-detail-journey-cell">
          <Col md="8">
            <h2 className="payment-detail-subtitle-header mt-2">{driverType}</h2>
            <p className="payment-detail-subtitle">{driverName}</p>
          </Col>
        </Row>

        {
          (payment.payment.status === PaymentStatus.PROCESSED) && (
            <Row className="payment-detail-journey-cell">
              <Col>
                <h2 className="payment-detail-subtitle-header mt-2">{t('payment.fields.mpesaTransactionId')}</h2>
                <p className="payment-detail-subtitle">{payment.payment.mpesaTransactionId}</p>
              </Col>
            </Row>
          )
        }
        {
          (payment.payment.status === PaymentStatus.PROCESSING_ERROR) && (
            <Row className="payment-detail-journey-cell">
              <Col>
                <h2 className="payment-detail-subtitle-header mt-2">{t('payment.detail.paymentProcessingError')}</h2>
                <p className="payment-detail-subtitle-danger">{payment.payment.processingError}</p>
              </Col>
            </Row>
          )
        }

        <Row className="payment-detail-journey-cell">
          <Col md="8">
            <h2 className="payment-detail-subtitle-header mt-2">{t('payment.fields.additionalNotes')}</h2>
            <p className="payment-detail-subtitle">{payment.payment.additionalNotes}</p>
          </Col>
        </Row>
        {
          (payment.payment.status !== PaymentStatus.PROCESSED && payment.payment.status !== PaymentStatus.REJECTED) && (
            <Row>
              <Col className="m-3">
                <Form>
                  <FormGroup>
                    <Label for="additional-notes">{t('payment.detail.editAdditionalNotes')}</Label>
                    <Input name="additionalNotes" id="additional-notes" onChange={handleAdditionalNotes} />
                  </FormGroup>
                </Form>
              </Col>
            </Row>
          )
        }

        <Row>
          <Col className="m-3">
            {
              !patchingPayment && (
                <>
                  {payment.payment.status !== PaymentStatus.PROCESSING_ERROR && payment.payment.status !== PaymentStatus.PROCESSED && payment.payment.status !== PaymentStatus.PROCESSED_OFFLINE && payment.payment.status !== PaymentStatus.REJECTED && (
                    <Button onClick={() => {
                      changeRequestStatus(PaymentStatus.REJECTED, watotocareSettingsDetail.paymentMethod);
                    }} className="btn btn-danger" style={{ margin: 10 }}>
                      {t('payment.detail.rejectRequest')}
                    </Button>
                  )}


                  {
                    (payment.payment.status === PaymentStatus.PROCESSING_ERROR) && (
                      <Button onClick={() => {
                        props.changePaymentStatus(payment.payment.id, PaymentStatus.APPROVED);
                      }} className="btn btn-danger" style={{ margin: 10 }}>
                        {t('payment.detail.changeStatusToApproved')}
                      </Button>
                    )
                  }

                  {(payment.payment.status === PaymentStatus.REQUESTED && ((hasAnyPermission(props.permissions, [PERMISSIONS.PAYMENTS_APPROVE]) &&
                    hasAnyPermission(props.permissions, [PERMISSIONS.USER_ADMIN])) || (props.isAdmin))) && (
                    <Button onClick={() => {
                      setPatchingPayment(true);
                      changeRequestStatus(PaymentStatus.APPROVED, PaymentStatus.APPROVED);
                    }} className="btn btn-primary" style={{ float: 'right', margin: 10 }}>
                      {t('payment.detail.approveRequest')}
                    </Button>
                  )}

                  {(payment.payment.status === PaymentStatus.APPROVED && (hasAnyPermission(props.permissions, [PERMISSIONS.PAYMENTS_PROCESS])
                    || hasAnyPermission(props.permissions, [PERMISSIONS.USER_ADMIN]))) && (
                    <>
                      <Button onClick={() => {
                        changeRequestStatus(PaymentStatus.PROCESSED_OFFLINE, 'OFFLINE');
                      }} className="btn btn-primary" style={{ float: 'right', margin: 10 }}>
                        {t('payment.detail.processPaymentOffline')}
                      </Button>

                      {watotocareSettingsDetail.paymentMethod !== 'OFFLINE' ?

                        <Button onClick={() => {
                          processOnline(PaymentStatus.PROCESSING);
                        }} className="btn btn-primary" style={{ float: 'right', margin: 10 }}>
                          {t('payment.detail.processPaymentOnline')}
                        </Button>
                        : ''
                      }
                    </>
                  )}
                  {
                    payment.payment.status === PaymentStatus.PROCESSING && (
                      <p style={{ textAlign: 'center' }}>{t('payment.detail.paymentProcessing')}</p>
                    )
                  }
                  {
                    payment.payment.status === PaymentStatus.PROCESSED || payment.payment.status === PaymentStatus.PROCESSED_OFFLINE && (
                      <p style={{ textAlign: 'center' }}>{t('payment.detail.paymentProcessed')}</p>
                    )
                  }
                  {
                    payment.payment.status === PaymentStatus.REJECTED && (
                      <p style={{ textAlign: 'center' }}>{t('payment.detail.paymentRejected')}</p>
                    )
                  }
                </>
              )
            }
          </Col>
        </Row>
      </LoadingOverlay>
    </>
  );
};

const mapStateToProps = ({ payment, watotocareSettings, authentication }: IRootState) => ({
  payment,
  watotocareSettings: watotocareSettings.settings,
  permissions: authentication.permissions,
  isAdmin: hasAnyPermission(authentication.account.roles, ['ADMIN'])
});

const mapDispatchToProps = {
  getPaymentAndPatientCase,
  patchPayment,
  processPaymentOnline,
  getSettings,
  changePaymentStatus
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(PaymentDetail);
